import { useMemo, useState, type JSX, type ReactNode } from "react";
import {
  Card,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  Inline,
  LoadingState,
  Stack,
  StatePill,
  TextInput,
  type DataTableColumn,
  type StatePillTone,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiGet, apiSend } from "../../../lib/api.js";
import { GtccCapHint } from "../../GtccCapHint.js";

interface SimRow {
  readonly id: string;
  readonly kind: "physical" | "esim" | "unknown";
  readonly iccid: string;
  readonly imsi: string | null;
  readonly msisdn: string | null;
  readonly provider: string | null;
  readonly status: string;
  readonly activationState: string | null;
  readonly roamingAllowed: boolean;
  readonly createdAt: string;
}

interface SimsResponse {
  readonly items: ReadonlyArray<SimRow>;
}

interface PoolDeviceRow {
  readonly id: string;
  readonly imei: string;
  readonly deviceType: string;
  readonly status: string;
  readonly createdAt: string;
}

interface PoolResponse {
  readonly items: ReadonlyArray<PoolDeviceRow>;
}

const POOL_QUERY_KEY = ["gtcc", "hw", "pool"] as const;
const POOL_DEVICE_TYPES = ["teltonika_codec8", "gt_pro_v0"] as const;

function PoolAssignAction({ row }: { readonly row: PoolDeviceRow }): JSX.Element {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const assign = useMutation({
    mutationFn: () =>
      apiSend("POST", `/v1/admin/managed-hardware/pool/${row.id}/assign`, {
        tenantId: tenantId.trim(),
      }),
    onSuccess: () => {
      setConfirmOpen(false);
      setFormOpen(false);
      setTenantId("");
      void queryClient.invalidateQueries({ queryKey: POOL_QUERY_KEY });
    },
    onError: (error) =>
      setError(
        isForbidden(error)
          ? t("admin.managed_hardware.error.forbidden_body")
          : t("admin.gtcc.hw.pool.actions.error"),
      ),
  });
  const tenantIdValid =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      tenantId.trim(),
    );

  return (
    <Stack gap={1}>
      <Button
        variant="secondary"
        disabled={assign.isPending}
        onClick={() => {
          setError(null);
          setFormOpen((open) => !open);
        }}
        data-testid={`gtcc-hw-pool-assign-${row.id}`}
      >
        {t("admin.gtcc.hw.pool.actions.assign")}
      </Button>
      {formOpen ? (
        <Inline gap={1} wrap>
          <TextInput
            label={t("admin.gtcc.hw.pool.actions.tenant_id")}
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
          />
          <Button
            variant="primary"
            disabled={assign.isPending || !tenantIdValid}
            onClick={() => {
              setError(null);
              setConfirmOpen(true);
            }}
            data-testid={`gtcc-hw-pool-assign-review-${row.id}`}
          >
            {t("admin.gtcc.hw.pool.actions.review_assign")}
          </Button>
        </Inline>
      ) : null}
      {error ? <span role="alert">{error}</span> : null}
      <ConfirmDialog
        open={confirmOpen}
        title={t("admin.gtcc.hw.pool.actions.assign")}
        body={t("admin.gtcc.hw.pool.actions.assign_confirm", {
          imei: row.imei,
          tenantId: tenantId.trim(),
        })}
        confirmLabel={t("admin.gtcc.hw.pool.actions.confirm_assign")}
        cancelLabel={t("common.cancel")}
        tone="default"
        pending={assign.isPending}
        errorMessage={error}
        onConfirm={() => assign.mutate()}
        onCancel={() => {
          if (!assign.isPending) setConfirmOpen(false);
        }}
        testIdPrefix={`gtcc-hw-pool-assign-confirm-${row.id}`}
      />
    </Stack>
  );
}

function SectionTitle({ children }: { readonly children: ReactNode }) {
  return (
    <h3
      style={{
        margin: 0,
        fontSize: "var(--gt-sys-font-size-md)",
        fontWeight: "var(--gt-sys-font-weight-semibold)",
      }}
    >
      {children}
    </h3>
  );
}

function SummaryGrid({ children }: { readonly children: ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(10rem, 1fr))",
        gap: "var(--gt-sys-space-3)",
      }}
    >
      {children}
    </div>
  );
}

function SummaryTile({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId: string;
}) {
  return (
    <Card elevation={0}>
      <Stack gap={1}>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
          }}
        >
          {label}
        </span>
        <strong
          data-testid={testId}
          style={{
            fontSize: "var(--gt-sys-font-size-lg)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </strong>
      </Stack>
    </Card>
  );
}

function stateTone(status: string | null | undefined): StatePillTone {
  const normalized = (status ?? "").toLowerCase();
  if (["activated", "active", "approved"].includes(normalized)) return "success";
  if (["paused", "suspended", "defective", "rejected"].includes(normalized)) return "danger";
  if (["ordered", "pending", "pending_approval"].includes(normalized)) return "warn";
  return "neutral";
}

function isForbidden(error: Error): boolean {
  return error instanceof ApiError && error.status === 403;
}

function ErrorBody({ error, message }: { readonly error: Error; readonly message: string }) {
  const { t } = useLocale();
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  return (
    <Stack gap={1}>
      <span>{message}</span>
      {requestId ? <code>{t("common.error.request_id", { id: requestId })}</code> : null}
    </Stack>
  );
}

function SimActions({ row }: { readonly row: SimRow }): JSX.Element {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"assign" | "edit" | null>(null);
  const [unassignOpen, setUnassignOpen] = useState(false);
  const [deviceId, setDeviceId] = useState("");
  const [provider, setProvider] = useState(row.provider ?? "");
  const [status, setStatus] = useState(row.status);
  const [error, setError] = useState<string | null>(null);
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["gtcc", "hw", "sims"] });
  };
  const assign = useMutation({
    mutationFn: () => apiSend("POST", `/v1/admin/sims/${row.id}/assign-device`, { deviceId }),
    onSuccess: () => {
      setMode(null);
      setDeviceId("");
      refresh();
    },
    onError: (error) =>
      setError(
        isForbidden(error)
          ? t("admin.managed_hardware.error.forbidden_body")
          : t("admin.gtcc.hw.sims.actions.error"),
      ),
  });
  const unassign = useMutation({
    mutationFn: () => apiSend("POST", `/v1/admin/sims/${row.id}/unassign-device`),
    onSuccess: () => {
      setUnassignOpen(false);
      refresh();
    },
    onError: (error) =>
      setError(
        isForbidden(error)
          ? t("admin.managed_hardware.error.forbidden_body")
          : t("admin.gtcc.hw.sims.actions.error"),
      ),
  });
  const edit = useMutation({
    mutationFn: () =>
      apiSend("PATCH", `/v1/admin/sims/${row.id}`, { provider: provider || null, status }),
    onSuccess: () => {
      setMode(null);
      refresh();
    },
    onError: (error) =>
      setError(
        isForbidden(error)
          ? t("admin.managed_hardware.error.forbidden_body")
          : t("admin.gtcc.hw.sims.actions.error"),
      ),
  });
  const pending = assign.isPending || unassign.isPending || edit.isPending;
  return (
    <Stack gap={1}>
      <Inline gap={1} wrap>
        <Button variant="primary" disabled={pending} onClick={() => setMode("assign")}>
          {t("admin.gtcc.hw.sims.actions.assign")}
        </Button>
        <Button variant="secondary" disabled={pending} onClick={() => setUnassignOpen(true)}>
          {t("admin.gtcc.hw.sims.actions.unassign")}
        </Button>
        <Button variant="secondary" disabled={pending} onClick={() => setMode("edit")}>
          {t("admin.gtcc.hw.sims.actions.edit")}
        </Button>
      </Inline>
      {mode === "assign" ? (
        <Inline gap={1}>
          <TextInput
            label={t("admin.gtcc.hw.sims.actions.device_id")}
            value={deviceId}
            onChange={(event) => setDeviceId(event.target.value)}
          />
          <Button
            variant="primary"
            disabled={pending || !deviceId.trim()}
            onClick={() => assign.mutate()}
          >
            {t("admin.gtcc.hw.sims.actions.confirm_assign")}
          </Button>
        </Inline>
      ) : null}
      {mode === "edit" ? (
        <Inline gap={1}>
          <TextInput
            label={t("admin.gtcc.hw.sims.actions.provider")}
            value={provider}
            onChange={(event) => setProvider(event.target.value)}
          />
          <TextInput
            label={t("admin.gtcc.hw.sims.actions.status")}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          />
          <Button variant="primary" disabled={pending} onClick={() => edit.mutate()}>
            {t("admin.gtcc.hw.sims.actions.confirm_edit")}
          </Button>
        </Inline>
      ) : null}
      {error ? <span role="alert">{error}</span> : null}
      <ConfirmDialog
        open={unassignOpen}
        title={t("admin.gtcc.hw.sims.actions.unassign")}
        body={t("admin.gtcc.hw.sims.actions.unassign_confirm")}
        confirmLabel={t("admin.gtcc.hw.sims.actions.unassign")}
        cancelLabel={t("common.cancel")}
        tone="danger"
        pending={unassign.isPending}
        errorMessage={error}
        onConfirm={() => unassign.mutate()}
        onCancel={() => {
          if (!unassign.isPending) setUnassignOpen(false);
        }}
        testIdPrefix={`gtcc-hw-sim-${row.id}-unassign`}
      />
    </Stack>
  );
}

export function SimsSection() {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [iccid, setIccid] = useState("");
  const [createProvider, setCreateProvider] = useState("");
  const [error, setError] = useState<string | null>(null);
  const query = useQuery<SimsResponse, Error>({
    queryKey: ["gtcc", "hw", "sims"],
    queryFn: ({ signal }) => apiGet<SimsResponse>("/v1/admin/sims", { limit: 200 }, signal),
    staleTime: 30_000,
  });
  const create = useMutation({
    mutationFn: () =>
      apiSend("POST", "/v1/admin/sims", {
        kind: "physical",
        iccid,
        provider: createProvider || null,
      }),
    onSuccess: () => {
      setCreateOpen(false);
      setIccid("");
      setCreateProvider("");
      void queryClient.invalidateQueries({ queryKey: ["gtcc", "hw", "sims"] });
    },
    onError: (error) =>
      setError(
        isForbidden(error)
          ? t("admin.managed_hardware.error.forbidden_body")
          : t("admin.gtcc.hw.sims.actions.error"),
      ),
  });

  const rows = useMemo(() => query.data?.items ?? [], [query.data?.items]);
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const filteredRows = useMemo(() => {
    if (normalizedSearch.length === 0) return rows;
    return rows.filter((row) =>
      [row.iccid, row.imsi, row.msisdn, row.provider, row.kind, row.status, row.activationState]
        .filter((value): value is string => typeof value === "string")
        .some((value) => value.toLocaleLowerCase().includes(normalizedSearch)),
    );
  }, [normalizedSearch, rows]);

  const columns: ReadonlyArray<DataTableColumn<SimRow>> = [
    {
      id: "iccid",
      header: t("admin.gtcc.hw.sims.col.iccid"),
      cell: (row) => <code>{row.iccid}</code>,
    },
    { id: "kind", header: t("admin.gtcc.hw.sims.col.kind"), cell: (row) => row.kind },
    {
      id: "provider",
      header: t("admin.gtcc.hw.sims.col.provider"),
      cell: (row) => row.provider ?? "—",
    },
    {
      id: "status",
      header: t("admin.gtcc.hw.sims.col.status"),
      cell: (row) => <StatePill label={row.status ?? "–"} tone={stateTone(row.status)} />,
    },
    {
      id: "activation",
      header: t("admin.gtcc.hw.sims.col.activation"),
      cell: (row) => row.activationState ?? "—",
    },
    {
      id: "actions",
      header: t("admin.gtcc.hw.sims.actions.column"),
      cell: (row) => <SimActions row={row} />,
    },
  ];

  return (
    <section data-testid="gtcc-hw-sims">
      <Card elevation={1}>
        <Stack gap={3}>
          <Inline gap={2} justify="space-between" align="center" wrap>
            <SectionTitle>{t("admin.gtcc.hw.sims.section")}</SectionTitle>
            <Button variant="primary" onClick={() => setCreateOpen((open) => !open)}>
              {t("admin.gtcc.hw.sims.actions.create")}
            </Button>
          </Inline>
          {createOpen ? (
            <Inline gap={1} wrap>
              <TextInput
                label={t("admin.gtcc.hw.sims.actions.iccid")}
                value={iccid}
                onChange={(event) => setIccid(event.target.value)}
              />
              <TextInput
                label={t("admin.gtcc.hw.sims.actions.provider")}
                value={createProvider}
                onChange={(event) => setCreateProvider(event.target.value)}
              />
              <Button
                variant="primary"
                disabled={create.isPending || iccid.trim().length < 8}
                onClick={() => create.mutate()}
              >
                {t("admin.gtcc.hw.sims.actions.confirm_create")}
              </Button>
            </Inline>
          ) : null}
          {error ? <span role="alert">{error}</span> : null}
          {query.isPending ? <LoadingState label={t("admin.gtcc.hw.sims.loading")} /> : null}
          {query.isError ? (
            <ErrorState
              title={
                isForbidden(query.error)
                  ? t("admin.gtcc.hw.sims.forbidden.title")
                  : t("admin.gtcc.hw.sims.error.title")
              }
              body={
                <ErrorBody
                  error={query.error}
                  message={
                    isForbidden(query.error)
                      ? t("admin.gtcc.hw.sims.forbidden.body")
                      : t("admin.gtcc.hw.sims.error.body")
                  }
                />
              }
              severity={isForbidden(query.error) ? "warn" : "error"}
              action={
                isForbidden(query.error) ? undefined : (
                  <Button variant="secondary" onClick={() => void query.refetch()}>
                    {t("common.retry")}
                  </Button>
                )
              }
            />
          ) : null}
          {query.isSuccess ? (
            <>
              <SummaryGrid>
                <SummaryTile
                  label={t("admin.gtcc.hw.sims.summary.total")}
                  value={String(rows.length)}
                  testId="gtcc-hw-sims-total"
                />
                <SummaryTile
                  label={t("admin.gtcc.hw.sims.summary.active")}
                  value={String(rows.filter((row) => row.status === "activated").length)}
                  testId="gtcc-hw-sims-active"
                />
                <SummaryTile
                  label={t("admin.gtcc.hw.sims.summary.blocked")}
                  value={String(
                    rows.filter((row) => ["paused", "suspended"].includes(row.status)).length,
                  )}
                  testId="gtcc-hw-sims-blocked"
                />
                <SummaryTile
                  label={t("admin.gtcc.hw.sims.summary.unassigned")}
                  value={t("admin.gtcc.hw.sims.summary.unassigned_unavailable")}
                  testId="gtcc-hw-sims-unassigned"
                />
              </SummaryGrid>
              <GtccCapHint limit={200} shown={rows.length} testId="gtcc-hw-sims-cap" />
              {rows.length === 0 ? (
                <EmptyState
                  title={t("admin.gtcc.hw.sims.empty.title")}
                  body={t("admin.gtcc.hw.sims.empty.body")}
                />
              ) : (
                <Stack gap={2}>
                  <TextInput
                    label={t("admin.gtcc.hw.sims.search.label")}
                    placeholder={t("admin.gtcc.hw.sims.search.placeholder")}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  <DataTable
                    caption={t("admin.gtcc.hw.sims.table.caption")}
                    columns={columns}
                    rows={filteredRows}
                    getRowId={(row) => row.id}
                    testId="gtcc-hw-sims-table"
                    empty={{
                      title: t("admin.gtcc.hw.sims.search.empty.title"),
                      body: t("admin.gtcc.hw.sims.search.empty.body"),
                    }}
                  />
                </Stack>
              )}
            </>
          ) : null}
        </Stack>
      </Card>
    </section>
  );
}

export function HardwareSection() {
  const { t, locale } = useLocale();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [imei, setImei] = useState("");
  const [deviceType, setDeviceType] = useState<(typeof POOL_DEVICE_TYPES)[number]>(
    POOL_DEVICE_TYPES[0],
  );
  const [createError, setCreateError] = useState<string | null>(null);
  const query = useQuery<PoolResponse, Error>({
    queryKey: POOL_QUERY_KEY,
    queryFn: ({ signal }) =>
      apiGet<PoolResponse>("/v1/admin/managed-hardware/pool", { limit: 200 }, signal),
    staleTime: 30_000,
  });
  const create = useMutation({
    mutationFn: () =>
      apiSend("POST", "/v1/admin/managed-hardware/pool", { imei: imei.trim(), deviceType }),
    onSuccess: () => {
      setCreateOpen(false);
      setImei("");
      setDeviceType(POOL_DEVICE_TYPES[0]);
      void queryClient.invalidateQueries({ queryKey: POOL_QUERY_KEY });
    },
    onError: (error) =>
      setCreateError(
        isForbidden(error)
          ? t("admin.managed_hardware.error.forbidden_body")
          : t("admin.gtcc.hw.pool.actions.error"),
      ),
  });
  const rows = query.data?.items ?? [];
  const countsByStatus = rows.reduce<Record<string, number>>((counts, row) => {
    counts[row.status] = (counts[row.status] ?? 0) + 1;
    return counts;
  }, {});
  const dateTime = new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" });

  const columns: ReadonlyArray<DataTableColumn<PoolDeviceRow>> = [
    {
      id: "imei",
      header: t("admin.gtcc.hw.pool.col.imei"),
      cell: (row) => <code>{row.imei}</code>,
    },
    {
      id: "type",
      header: t("admin.gtcc.hw.pool.col.type"),
      cell: (row) => row.deviceType,
    },
    {
      id: "status",
      header: t("admin.gtcc.hw.pool.col.status"),
      cell: (row) => <StatePill label={row.status ?? "–"} tone={stateTone(row.status)} />,
    },
    {
      id: "created",
      header: t("admin.gtcc.hw.pool.col.created"),
      cell: (row) => {
        const value = new Date(row.createdAt);
        return Number.isNaN(value.valueOf()) ? (
          "—"
        ) : (
          <time dateTime={row.createdAt}>{dateTime.format(value)}</time>
        );
      },
    },
    {
      id: "actions",
      header: t("admin.gtcc.hw.pool.actions.column"),
      cell: (row) => <PoolAssignAction row={row} />,
    },
  ];

  return (
    <section data-testid="gtcc-hw-pool">
      <Card elevation={1}>
        <Stack gap={3}>
          <Inline gap={2} justify="space-between" align="center" wrap>
            <SectionTitle>{t("admin.gtcc.hw.pool.section")}</SectionTitle>
            <Button
              variant="primary"
              disabled={create.isPending}
              onClick={() => {
                setCreateError(null);
                setCreateOpen((open) => !open);
              }}
              data-testid="gtcc-hw-pool-create"
            >
              {t("admin.gtcc.hw.pool.actions.create")}
            </Button>
          </Inline>
          {createOpen ? (
            <Stack gap={1}>
              <Inline gap={1} wrap>
                <TextInput
                  label={t("admin.gtcc.hw.pool.actions.imei")}
                  value={imei}
                  onChange={(event) => setImei(event.target.value)}
                  inputMode="numeric"
                />
                <label>
                  <span>{t("admin.gtcc.hw.pool.actions.device_type")}</span>
                  <select
                    value={deviceType}
                    onChange={(event) =>
                      setDeviceType(event.target.value as (typeof POOL_DEVICE_TYPES)[number])
                    }
                  >
                    {POOL_DEVICE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </label>
                <Button
                  variant="primary"
                  disabled={create.isPending || !/^\d{15}$/.test(imei.trim())}
                  onClick={() => {
                    setCreateError(null);
                    create.mutate();
                  }}
                  data-testid="gtcc-hw-pool-create-confirm"
                >
                  {t("admin.gtcc.hw.pool.actions.confirm_create")}
                </Button>
              </Inline>
              {createError ? <span role="alert">{createError}</span> : null}
            </Stack>
          ) : null}
          {query.isPending ? <LoadingState label={t("admin.gtcc.hw.pool.loading")} /> : null}
          {query.isError ? (
            <ErrorState
              title={
                isForbidden(query.error)
                  ? t("admin.gtcc.hw.pool.forbidden.title")
                  : t("admin.gtcc.hw.pool.error.title")
              }
              body={
                <ErrorBody
                  error={query.error}
                  message={
                    isForbidden(query.error)
                      ? t("admin.gtcc.hw.pool.forbidden.body")
                      : t("admin.gtcc.hw.pool.error.body")
                  }
                />
              }
              severity={isForbidden(query.error) ? "warn" : "error"}
              action={
                isForbidden(query.error) ? undefined : (
                  <Button variant="secondary" onClick={() => void query.refetch()}>
                    {t("common.retry")}
                  </Button>
                )
              }
            />
          ) : null}
          {query.isSuccess ? (
            <>
              <SummaryGrid>
                <SummaryTile
                  label={t("admin.gtcc.hw.pool.summary.total")}
                  value={String(rows.length)}
                  testId="gtcc-hw-pool-total"
                />
                {Object.entries(countsByStatus)
                  .sort(([left], [right]) => left.localeCompare(right))
                  .map(([status, count]) => (
                    <SummaryTile
                      key={status}
                      label={status}
                      value={String(count)}
                      testId={`gtcc-hw-pool-status-${status}`}
                    />
                  ))}
              </SummaryGrid>
              <GtccCapHint limit={200} shown={rows.length} testId="gtcc-hw-pool-cap" />
              {rows.length === 0 ? (
                <EmptyState
                  title={t("admin.gtcc.hw.pool.empty.title")}
                  body={t("admin.gtcc.hw.pool.empty.body")}
                />
              ) : (
                <DataTable
                  caption={t("admin.gtcc.hw.pool.table.caption")}
                  columns={columns}
                  rows={rows}
                  getRowId={(row) => row.id}
                  testId="gtcc-hw-pool-table"
                />
              )}
            </>
          ) : null}
        </Stack>
      </Card>
    </section>
  );
}

export function GtccHardwareView(): JSX.Element {
  const { t } = useLocale();
  return (
    <Stack gap={4}>
      <Stack gap={1}>
        <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
          {t("admin.gtcc.hw.title")}
        </h2>
        <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
          {t("admin.gtcc.hw.subtitle")}
        </p>
      </Stack>
      <SimsSection />
      <HardwareSection />
      <Inline>
        <span
          style={{
            color: "var(--gt-sys-text-muted)",
            fontSize: "var(--gt-sys-font-size-xs)",
          }}
        >
          {t("admin.gtcc.hw.read_only_note")}
        </span>
      </Inline>
    </Stack>
  );
}
